import type { SourceAssessmentSnapshot } from '../../services/api/types.gen';
import { formatDateTimeInPT } from '../../utils/dateUtils';

/** Source-only observations. Never passed to the editable assessment form. */
export default function SourceAssessmentEvidence({
  snapshots,
}: {
  snapshots?: SourceAssessmentSnapshot[] | null;
}) {
  if (!snapshots?.length) return null;
  const ordered = [...snapshots].sort(
    (a, b) => Date.parse(b.captured_at) - Date.parse(a.captured_at)
  );
  return (
    <section
      aria-label='Historical assessment evidence'
      className='rounded-lg border border-border bg-muted/20 p-4 text-sm'
    >
      <h3 className='font-medium'>Historical assessment · source evidence</h3>
      <p className='mt-1 text-muted-foreground'>
        Preserved from External EMR. These historical codes do not populate or
        complete the Roger assessment. Full source coverage is unknown.
      </p>
      <ol className='mt-3 space-y-3'>
        {ordered.map((snapshot, index) => (
          <li key={`${snapshot.captured_at}:${index}`}>
            <details className='rounded-md border border-border/60 bg-background p-3'>
              <summary className='cursor-pointer font-medium'>
                {snapshot.instrument} ·{' '}
                {snapshot.state === 'failed'
                  ? 'Recovery failed'
                  : 'Partial recovery'}
                <span className='block text-xs font-normal text-muted-foreground'>
                  Captured {formatDateTimeInPT(snapshot.captured_at)} Pacific
                </span>
              </summary>
              {snapshot.state === 'failed' ? (
                <p className='mt-3'>
                  This attempt could not recover structured answers. It does not
                  mean the source assessment was empty. Other captures, when
                  available, are listed separately.
                </p>
              ) : (
                <>
                  <p className='mt-3 text-muted-foreground'>
                    {Object.keys(snapshot.items ?? {}).length} explicit choices
                    recovered; {snapshot.unsupported_items?.length ?? 0}{' '}
                    observed fields could not be decoded. These counts do not
                    measure the whole assessment.
                  </p>
                  <div className='mt-3 overflow-x-auto'>
                    <table className='w-full text-left text-xs'>
                      <caption className='sr-only'>
                        Original source codes
                      </caption>
                      <thead>
                        <tr className='border-b'>
                          <th scope='col' className='py-2 pr-4 font-medium'>
                            Source item
                          </th>
                          <th scope='col' className='py-2 font-medium'>
                            Selected source code
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(snapshot.items ?? {})
                          .sort(([a], [b]) => a.localeCompare(b))
                          .map(([item, code]) => (
                            <tr
                              key={item}
                              className='border-b border-border/40'
                            >
                              <th
                                scope='row'
                                className='py-2 pr-4 font-mono font-normal [overflow-wrap:anywhere]'
                              >
                                {item}
                              </th>
                              <td className='py-2 font-mono'>{code}</td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                  {!!snapshot.unsupported_items?.length && (
                    <details className='mt-3 text-xs'>
                      <summary className='cursor-pointer'>
                        Fields not decoded
                      </summary>
                      <ul className='mt-2 space-y-1 font-mono [overflow-wrap:anywhere]'>
                        {snapshot.unsupported_items.map(item => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    </details>
                  )}
                </>
              )}
              {snapshot.source_url && (
                <a
                  className='mt-3 inline-block underline underline-offset-2'
                  href={snapshot.source_url}
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  Open source task in External EMR
                </a>
              )}
            </details>
          </li>
        ))}
      </ol>
    </section>
  );
}
